import React from 'react';
import { Battery, Cpu, Zap, Shield, Clock, Award } from 'lucide-react';

const Hardware = () => {
  const hardwareItems = [
    {
      icon: Zap,
      title: 'Paneles Solares Premium',
      description: 'Paneles Jinko de alta eficiencia con tecnología monocristalina. Máxima generación de energía incluso en días nublados.',
      specs: [
        '650W de potencia por panel',
        'Garantía de rendimiento 25 años',
        'Resistente a granizo y vientos fuertes',
      ],
      image: 'https://customer-assets.emergentagent.com/job_3a22e1e9-596e-48c2-b9b4-3588e12d7d49/artifacts/v6ctyknb_Captura%20de%20pantalla%202026-03-31%20a%20la%28s%29%207.18.59%E2%80%AFp.%E2%80%AFm..png',
    },
    {
      icon: Cpu,
      title: 'Inversores Inteligentes',
      description: 'Inversores SunGrow de última generación con monitoreo en tiempo real y máxima eficiencia de conversión.',
      specs: [
        'Monitoreo vía app móvil',
        'Protección contra sobretensiones',
        'Garantía 10 años',
      ],
      image: 'https://customer-assets.emergentagent.com/job_3a22e1e9-596e-48c2-b9b4-3588e12d7d49/artifacts/h7wn2htv_1824-2_634764305e372.jpeg',
    },
    {
      icon: Battery,
      title: 'Almacenamiento de Energía',
      description: 'Baterías SunGrow de litio de alta capacidad para almacenar energía y usarla cuando más lo necesites.',
      specs: [
        'Capacidad desde 10kWh',
        'Más de 6000 ciclos de carga',
        'Integración con red eléctrica',
      ],
      image: 'https://customer-assets.emergentagent.com/job_3a22e1e9-596e-48c2-b9b4-3588e12d7d49/artifacts/pjuoqiwj_%5BGW-LX%20U5.0-30%5D%20GoodWe%20Lynx%20U%20G3%20Series%2C%20LX%20U5.0-30%2C%20Low%20Voltage%20Battery.jpg',
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Garantía Extendida',
      description: 'Hasta 25 años en paneles y 10 años en inversores',
    },
    {
      icon: Award,
      title: 'Certificación Internacional',
      description: 'Equipos certificados por IEC, UL y TUV',
    },
    {
      icon: Clock,
      title: 'Instalación Rápida',
      description: 'Sistemas operativos en 3-7 días hábiles',
    },
  ];

  return (
    <section id="hardware" className="pt-16 sm:pt-20 md:pt-24 pb-10 sm:pb-12 md:pb-16 bg-zinc-900 text-white" data-testid="hardware-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
            Ecosistema Premium
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-4 sm:mb-6">
            Tecnologia de Vanguardia
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto px-2">
            Utilizamos sólo los mejores equipos del mercado, con certificaciones internacionales 
            y garantías extendidas para tu tranquilidad.
          </p>
        </div>

        {/* Hardware Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {hardwareItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="bg-zinc-800 border border-zinc-700 hover:border-[#90EE90] transition-all duration-300 overflow-hidden group"
                data-testid={`hardware-card-${index}`}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-white">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <Icon className="text-[#90EE90]" size={36} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-6">{item.description}</p>

                  {/* Specs */}
                  <ul className="space-y-2">
                    {item.specs.map((spec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full mt-1.5 flex-shrink-0"></div>
                        <span>{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-zinc-800 pt-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div key={index} className="flex items-start gap-4">
                <div className="bg-[#90EE90]/10 p-3 border border-[#90EE90]/30">
                  <Icon className="text-[#90EE90]" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-1">{benefit.title}</h4>
                  <p className="text-sm text-zinc-400">{benefit.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#90EE90] text-[#14532D] hover:bg-[#7CDF7C] px-8 py-4 font-bold transition-all duration-300"
            data-testid="hardware-contact-cta"
          >
            Solicitar Información Técnica
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hardware;